'use client';

import { useCallback } from 'react';
import { trpc } from '@/app/api/trpc/client';
import { useWorkoutStore } from '@/app/stores/workoutStore';
import type { WorkoutWithRelations } from '@/utils/types';
import { toast } from 'react-toastify';

interface UseGarminSyncButtonProps {
  workout: WorkoutWithRelations;
  onWorkoutUpdate?: (updatedWorkout: WorkoutWithRelations) => void;
}

export const useGarminSyncButton = ({
  workout,
  onWorkoutUpdate,
}: UseGarminSyncButtonProps) => {
  const utils = trpc.useUtils();
  const mergeWorkouts = useWorkoutStore((state) => state.mergeWorkouts);

  const { mutate: pushWorkout, isPending: isPushing } =
    trpc.workouts.pushWorkoutToGarmin.useMutation({
      onSuccess: () => {
        toast.success('Workout pushed to Garmin');
      },
      onError: (error) => {
        toast.error(
          `Failed to push workout to Garmin: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        );
      },
    });

  const { mutate: removeWorkout, isPending: isRemoving } =
    trpc.workouts.removeWorkoutFromGarmin.useMutation({
      onSuccess: async () => {
        toast.success('Workout removed from Garmin');
        await utils.workouts.getMany.invalidate();
      },
      onError: (error) => {
        toast.error(
          `Failed to remove workout from Garmin: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        );
      },
    });

  const isSynced = Boolean(workout.garminScheduleId || workout.garminWorkoutId);
  const isLoading = isSynced ? isRemoving : isPushing;

  const handleGarminAction = useCallback(() => {
    if (!workout.id) {
      toast.error('Save the workout before performing Garmin actions.');
      return;
    }

    if (isSynced) {
      // Unsync action
      const cleanedWorkout: WorkoutWithRelations = {
        ...workout,
        garminWorkoutId: null,
        garminScheduleId: null,
      };

      removeWorkout(
        { workoutId: workout.id },
        {
          onSuccess: () => {
            mergeWorkouts([cleanedWorkout]);
            onWorkoutUpdate?.(cleanedWorkout);
          },
          onError: (error) => {
            // Error toast is already handled in removeWorkout mutation
            console.error('Failed to unsync from Garmin:', error);
          },
        }
      );
    } else {
      // Sync action
      pushWorkout(
        { workoutId: workout.id },
        {
          onSuccess: async () => {
            try {
              const updatedWorkout = (await utils.workouts.getById.fetch({
                id: workout.id,
              })) as WorkoutWithRelations;
              mergeWorkouts([updatedWorkout]);
              onWorkoutUpdate?.(updatedWorkout);
            } catch (error) {
              console.error(
                'Failed to refresh workout after Garmin sync:',
                error
              );
              toast.error(
                'Workout synced to Garmin, but failed to update local view.'
              );
            }
          },
          onError: (error) => {
            // Error toast is already handled in pushWorkout mutation
            console.error('Failed to sync to Garmin:', error);
          },
        }
      );
    }
  }, [
    workout,
    isSynced,
    removeWorkout,
    pushWorkout,
    mergeWorkouts,
    onWorkoutUpdate,
    utils.workouts.getById,
  ]);

  return {
    isSynced,
    isLoading,
    handleGarminAction,
    buttonTitle: isSynced ? 'Unsync Garmin' : 'Sync Garmin',
    // These class names are for the _icon and text_ color. Button styling is external.
    colorClassName: isSynced ? 'text-emerald-500' : 'text-orange-500',
    // Tailwind classes for button border and text color for workout editor (specific styles)
    buttonColorClasses: isSynced
      ? 'border-emerald-500 text-emerald-500 hover:bg-emerald-50'
      : 'border-orange-500 text-orange-500 hover:bg-orange-50',
  };
};
